'use client';

import type { WidgetConfigState } from '../fields';
import {
    BROWSER_CALLABLE_NOTE,
    KALSHI_DOCS_URL,
    SERVER_SIDE_ONLY_NOTE,
    resolveDataRequests,
    type DataRequest,
} from '../dataSource';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard';
import type { WidgetSchema } from '../types';
import { buttonVariants } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { useOrigin } from '../../lib/useOrigin';

const curlFor = (request: DataRequest) => `curl -s '${request.url}'`;

function RequestRow({ request }: { request: DataRequest }) {
    const { copy, copied } = useCopyToClipboard();
    return (
        <li className="rounded-x40 border border-stroke-x30 bg-surface-x10 p-4">
            <div className="mb-2 flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                    <span className="rounded-x15 bg-container-x30 px-1.5 py-0.5 font-mono text-[10px] font-medium text-text-x10">
                        GET
                    </span>
                    <span className="typ-emphasis-x10 truncate text-text-x10">{request.label}</span>
                </div>
                <button
                    type="button"
                    onClick={() => copy(curlFor(request))}
                    className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
                >
                    {copied ? 'Copied' : 'Copy curl'}
                </button>
            </div>
            <p className="mb-2 break-all font-mono text-[12px] leading-relaxed text-text-x10">{request.url}</p>
            <p className="typ-body-x10 text-text-x20">{request.returns}</p>
        </li>
    );
}

/**
 * Shows the endpoints behind a widget so a partner can rebuild it with their own rendering
 * instead of embedding the iframe. Rendered only for widgets whose schema declares a
 * `dataSource`.
 */
export function DataSourceBlock({ schema, config }: { schema: WidgetSchema; config: WidgetConfigState }) {
    const source = schema.dataSource;
    // The compute endpoints live on this app, so their URLs depend on where it is served from.
    const origin = useOrigin('https://widgets.kalshi.com');

    if (!source) return null;
    const requests = resolveDataRequests(source, config, origin);
    if (requests.length === 0) return null;

    return (
        <section>
            <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
                <p className="typ-overline-x10 text-text-x30">Build your own</p>
                <a
                    href={source.docsUrl ?? KALSHI_DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="typ-body-x10 text-text-x20 underline-link hover:text-text-x10"
                >
                    API reference
                </a>
            </div>

            <p className="typ-body-x20 mb-4 max-w-[70ch] text-text-x20">{source.summary}</p>

            <ul className="mb-4 space-y-3">
                {requests.map((request) => (
                    <RequestRow key={request.url} request={request} />
                ))}
            </ul>

            <div className="rounded-x40 border border-stroke-x30 bg-surface-foregroundOpaque p-4">
                <p className="typ-emphasis-x10 mb-1 text-text-x10">
                    {source.browserCallable ? 'Call these from anywhere' : 'Call these from your server'}
                </p>
                <p className="typ-body-x10 text-text-x20">
                    {source.browserCallable ? BROWSER_CALLABLE_NOTE : SERVER_SIDE_ONLY_NOTE}
                </p>
                {source.notes?.length ? (
                    <ul className="typ-body-x10 mt-2 list-disc space-y-1 pl-4 text-text-x20">
                        {source.notes.map((note) => (
                            <li key={note}>{note}</li>
                        ))}
                    </ul>
                ) : null}
            </div>
        </section>
    );
}
